import {
  DateSelectArg,
  EventClickArg,
  EventContentArg,
} from "@fullcalendar/core";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import FullCalendar from "@fullcalendar/react";
import timeGridPlugin from "@fullcalendar/timegrid";
import { Button, Grid, useMediaQuery, useTheme } from "@mui/material";
import { FC, useCallback, useMemo, useState } from "react";
import useFullCalendarQuery from "./FullCalendarQuery/useFullCalendarQuery";
import {
  useCalendarDispatch,
  useCalendarState,
} from "./context/CalendarContext";
import { refreshCalendar } from "./context/utils";
import useFullCalendarHandlers from "./fullCalendarHandlers/useFullCalendarHandlers";
import ScheduleModal from "./scheduleModal/ScheduleModal";
import { StyledCalendarCard } from "./styles";

interface CalendarProps {}
type DateSelection = DateSelectArg | Date;

const Calendar: FC<CalendarProps> = () => {
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));
  const isMdDown = useMediaQuery(theme.breakpoints.down("md"));

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [dataSelected, setDataSelected] = useState<string | Date>("");
  const [selectedEvent, setSelectedEvent] = useState<string | null>(null);

  const calendarState = useCalendarState();
  const calendarDispatch = useCalendarDispatch();

  const { events } = calendarState;

  useFullCalendarQuery();

  const getSanitizedEvents = useCallback(() => {
    return events || [];
  }, [events]);

  const sanitizedEvents = useMemo(
    () => getSanitizedEvents(),
    [getSanitizedEvents]
  );

  const { handleDateSet, eventDidMount } = useFullCalendarHandlers();

  const handleDateSelect = (selectInfo: DateSelection) => {
    if (selectInfo instanceof Date) {
      setDataSelected(selectInfo);
      openAgendamentoModal();
      return;
    }

    if (selectInfo.startStr) {
      setDataSelected(selectInfo.startStr);
    } else {
      setDataSelected(new Date());
    }

    openAgendamentoModal();
  };

  const openAgendamentoModal = () => {
    setIsModalOpen(true);
  };

  const handleEventClick = (clickInfo: EventClickArg) => {
    if (clickInfo.event) {
      setSelectedEvent(clickInfo.event._def.publicId);
      setIsModalOpen(true);
    }
  };

  const renderEventContent = (eventContent: EventContentArg) => {
    return (
      <>
        <b>{eventContent.timeText}</b>
        <i>{eventContent.event.title}</i>
      </>
    );
  };

  const handleClose = (needsUpdate: boolean) => {
    setIsModalOpen(false);
    setSelectedEvent(null);
    if (needsUpdate) {
      refreshCalendar(calendarDispatch);
    }
  };

  const headerToolbar = useMemo(() => {
    if (isSmDown) {
      return {
        left: "title",
        center: "",
        right: "prev,next",
      };
    }

    if (isMdDown) {
      return {
        left: "title",
        center: "prev,next today",
        right: "",
      };
    }

    return {
      left: "prev,next today",
      center: "title",
      right: "dayGridMonth,timeGridWeek,timeGridDay",
    };
  }, [isMdDown, isSmDown]);

  const footerToolbar = useMemo(() => {
    if (isSmDown) {
      return {
        left: "today",
        center: "",
        right: "dayGridMonth,timeGridWeek,timeGridDay",
      };
    }

    if (isMdDown) {
      return {
        left: "",
        center: "dayGridMonth,timeGridWeek,timeGridDay",
        right: "",
      };
    }

    return false;
  }, [isMdDown, isSmDown]);

  return (
    <StyledCalendarCard sx={{ p: { xs: 1.5, md: 2 } }}>
      <Grid
        container
        justifyContent="flex-end"
        sx={{ mt: "auto", mb: 1, width: "100%" }}
      >
        <Button
          variant="contained"
          onClick={() => {
            handleDateSelect(new Date());
            setIsModalOpen(true);
          }}
          sx={{
            width: { xs: "100%", sm: "auto" },
            minHeight: 48,
          }}
        >
          Novo Agendamento
        </Button>
      </Grid>

      <FullCalendar
        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
        headerToolbar={headerToolbar}
        footerToolbar={footerToolbar}
        buttonText={{
          today: "Hoje",
          month: "Mês",
          week: "Semana",
          day: "Dia",
          list: "Lista",
        }}
        locale="pt-br"
        timeZone="America/Sao_Paulo"
        slotLabelFormat={{
          hour: "numeric",
          minute: "2-digit",
        }}
        eventTimeFormat={{
          hour: "numeric",
          minute: "2-digit",
          meridiem: false,
        }}
        allDayText="Dia todo"
        initialView="dayGridMonth"
        editable={true}
        selectable={true}
        selectMirror={true}
        dayMaxEvents={true}
        weekends={true}
        events={sanitizedEvents}
        datesSet={handleDateSet}
        select={handleDateSelect}
        eventContent={renderEventContent}
        eventClick={handleEventClick}
        eventDidMount={eventDidMount}
      />
      {isModalOpen && (
        <ScheduleModal
          open={isModalOpen}
          handleClose={handleClose}
          dataSelected={dataSelected}
          selectedEvent={selectedEvent}
        />
      )}
    </StyledCalendarCard>
  );
};

export default Calendar;
